import React, { useState } from 'react';
import axios from 'axios';
import cn from '@/utils/cn';
import { UnstyledButton } from '@/components/ui/Buttons';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { COLORS } from '@/components/ui/ui.constants';
import I18n from '@/modules/i18n';
import * as Routes from '@/modules/routes';

export interface OrphanedAiData {
  classroom_count: number;
  dismissed: boolean;
  first_classroom_id: number;
  student_count: number;
}

interface AIReadingLevelBannerProps {
  orphanedAiData: OrphanedAiData | null;
  programId: number;
}

const AIReadingLevelBanner = ({ orphanedAiData, programId }: AIReadingLevelBannerProps) => {
  const [hidden, setHidden] = useState(false);

  if (!orphanedAiData || orphanedAiData.dismissed || hidden) return null;

  const classroomCount = orphanedAiData.classroom_count;
  const firstClassroomId = orphanedAiData.first_classroom_id;
  const studentCount = orphanedAiData.student_count;

  let bodyText: string;
  if (classroomCount > 1) {
    bodyText = I18n.t('ai_reading_level_banner_body_multi_class', {
      class_count: classroomCount,
      student_count: studentCount,
    });
  } else if (studentCount === 1) {
    bodyText = I18n.t('ai_reading_level_banner_body_single_class_one');
  } else {
    bodyText = I18n.t('ai_reading_level_banner_body_single_class', { count: studentCount });
  }

  const handleDismiss = () => {
    setHidden(true);
    axios
      .post(Routes.plato_api_dismiss_ai_reading_level_banner_settings_path(), { program_id: programId })
      .catch(() => setHidden(false));
  };

  return (
    <div
      className={cn(
        'tw-relative tw-flex tw-items-start tw-gap-3',
        'tw-rounded-[10px] tw-border tw-border-solid tw-border-orange tw-bg-light-orange',
        'tw-px-4 tw-py-3 tw-my-4',
      )}
      role="alert"
    >
      <Icon className="tw-shrink-0 tw-mt-0.5" color={COLORS['tci-black']} icon="Warning" size={24} />
      <div className="tw-flex-1">
        <Text className="tw-text-tci-black" weight="bold">
          {I18n.t('ai_reading_level_banner_headline')}
        </Text>
        <Text className="tw-text-very-dark-grey tw-mt-0.5" size="sm">
          {bodyText}
        </Text>
        <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2">
          <a
            className="btn btn--sm btn--outline-purple tw-no-underline"
            href={Routes.teacher_classroom_students_path(firstClassroomId)}
          >
            {I18n.t('ai_reading_level_banner_review')}
          </a>
          <UnstyledButton onClick={handleDismiss}>
            <Text className="!tw-text-tci-purple" size="xs" tag="span" weight="bold">
              {I18n.t('ai_reading_level_banner_dismiss')}
            </Text>
          </UnstyledButton>
        </div>
      </div>
      <UnstyledButton
        aria-label="Close banner"
        className="tw-absolute tw-top-4 tw-right-4 tw-leading-none"
        onClick={handleDismiss}
      >
        <Icon color={COLORS['dark-grey']} icon="Close" size={20} />
      </UnstyledButton>
    </div>
  );
};

export default AIReadingLevelBanner;
